import React from "react";
import { useUiControl, ControlOverlay, useCopilot } from "@oppizi-ecosystem/copilot";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";

import CONFIG from "../config/config";
import { authorization } from "../module/auth";
import { getConversationId, subscribeConversationId } from "./api";
import { createControlTelemetry } from "./controlTelemetry";

/**
 * Copilot control mode, builder side.
 *
 * The agent cannot start driving on its own, and nothing is handed to this page
 * to make it trustworthy. The sequence is:
 *   1. the agent calls ui_request_control, which records the ask server-side;
 *   2. `useUiControl` notices (via `/pending`) and moves to the `asked` phase;
 *   3. `<ControlOverlay>` — not this component — owns the consent card and its
 *      three answers (standing / once / decline), authenticated by the page's
 *      OWN platform session, the same credential it already uses for every
 *      other call;
 *   4. the answer goes back into the conversation as an ordinary message, which
 *      is what starts the agent's next turn — a grant is server state, so
 *      without this the agent would sit waiting for a reply that never comes;
 *   5. from then on the executor polls/streams, acts and reports until Stop, a
 *      refusal budget, or the grant otherwise expires.
 *
 * Behind the `copilot-ui-control` flag: off is exactly today's behaviour — no
 * prompt, no polling, no listeners.
 */

/**
 * CRA bakes env vars at build time, so a missing REACT_APP_GATEWAY_BASE_URL
 * does not fail the build — it ships the localhost default and every control
 * call quietly goes nowhere. That is exactly what reached production on the
 * first deploy (2026-09-10): the page called http://localhost:3850 from
 * builder.oppizi.com and control mode did nothing at all, silently.
 *
 * So refuse to run rather than pretend: if the configured gateway is localhost
 * while the page is not, control mode stays off and says why. A broken
 * deploy should be loud in the console, not invisible in the product.
 */
const gatewayIsUsable = () => {
  const base = CONFIG.GATEWAY_BASE_URL || "";
  const onLocalhost = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  if (!base) return false;
  if (/localhost|127\.0\.0\.1/.test(base) && !onLocalhost) {
    console.error(
      "copilot control: REACT_APP_GATEWAY_BASE_URL is unset in this build, so " +
        "the control channel would call " +
        base +
        " from " +
        window.location.origin +
        ". Control mode is disabled."
    );
    return false;
  }
  return true;
};

// Best-effort campaign id for telemetry only — never used for authorization.
// The mount sits above the router (no route params here), so it is read
// straight off the URL, which both builder routes (`.../campaign/:campaignId/
// builder` and the ADM `.../adm/campaign/:campaignId/builder/:step?`) carry
// in the same position.
const getCampaignId = () => {
  const match = /\/campaign\/([^/]+)\/builder/.exec(window.location.pathname);
  return match ? match[1] : undefined;
};

// No shared `detectStep` helper exists yet (see WP-H2), so read the step
// anchor directly. Pages that opt into telemetry mark their root with
// `data-copilot-step`; everything else reports no step, which the telemetry
// module already tolerates (`getStep?.()` is optional).
const getStep = () => document.querySelector("[data-copilot-step]")?.dataset.copilotStep;

const UiControlMount = React.forwardRef((_props, ref) => {
  const flagEnabled = useFeatureFlagEnabled("copilot-ui-control");
  const posthog = usePostHog();
  // Hooks run unconditionally, ALWAYS. Guarding this one behind the flag
  // (`!!flagEnabled && useMemo(...)`) shipped React error #311 to production:
  // useFeatureFlagEnabled returns undefined on the first render and a boolean
  // on the next, so the hook count changed between renders and the component
  // threw. Compute both, then combine.
  const gatewayUsable = React.useMemo(gatewayIsUsable, []);
  const [conversationId, setConversationId] = React.useState(getConversationId);
  const enabled = !!flagEnabled && gatewayUsable && !!conversationId;

  // The session resolves asynchronously, so the id can arrive after this
  // mounts — and it CHANGES on a History pick or a new conversation
  // (sessionKey switch). Grants are bound to (conversationId, host, tabId), so
  // the channel follows the store for the whole lifetime of the mount: polling
  // `/pending` on a stale thread would never see the new thread's request.
  React.useEffect(() => {
    setConversationId(getConversationId());
    return subscribeConversationId(setConversationId);
  }, []);

  const channel = React.useMemo(
    () =>
      enabled
        ? {
            baseUrl: CONFIG.GATEWAY_BASE_URL,
            conversationId,
            // Read per call, never captured: a rotated or expired session must
            // not linger in a closure.
            getAuthToken: () => authorization.getAPIKey(),
          }
        : null,
    [enabled, conversationId]
  );

  const { sendText } = useCopilot();

  /**
   * Tell the agent what the person decided. The grant lives on the server and
   * the agent's turn has already ended, so the only thing that wakes it is a
   * message in the conversation — the same path the composer uses.
   */
  const reply = React.useCallback(
    (message) => {
      sendText?.(message).catch((error) => {
        console.error("copilot control: could not notify the agent:", error);
      });
    },
    [sendText]
  );

  const telemetry = React.useMemo(
    () => createControlTelemetry({ posthog, getConversationId, getCampaignId, getStep }),
    [posthog]
  );

  // The library emits `stopped` for every user-initiated stop — the Stop
  // button AND the Esc shortcut — and never for expiry. Replying here (not in
  // a wrapper around the button) is what makes Esc tell the agent too; the
  // first live run stopped with Esc and the agent was never told.
  const onEvent = React.useCallback(
    (event) => {
      telemetry(event);
      if (event?.name === "stopped") reply("Stop — I'll take it from here.");
    },
    [telemetry, reply]
  );

  const { state, respond, stop, pause, resume, retry, takeover, dismiss, toggleLog, checkPending } = useUiControl({
    enabled,
    channel,
    onEvent,
  });

  // `respond` is the overlay's consent answer (standing / once / decline). It
  // only resolves true when the grant/decline actually round-tripped to the
  // gateway — a failed call gets no chat message, since nothing changed for
  // the agent to react to.
  const respondAndReply = React.useCallback(
    async (answer) => {
      const ok = await respond(answer);
      if (!ok) return ok;
      if (answer === "conversation") reply("Go ahead — you can drive this page.");
      else if (answer === "once") reply("Go ahead — do this one step, then I'll take over.");
      else if (answer === "decline") reply("Not right now — just tell me what to click.");
      return ok;
    },
    [respond, reply]
  );

  // Parent nudges this on every agent message — that is when a request appears.
  React.useImperativeHandle(ref, () => ({ recheck: checkPending }), [checkPending]);

  React.useEffect(() => {
    if (!enabled) return;
    void checkPending();
  }, [enabled, checkPending]);

  if (!enabled) return null;

  return (
    <ControlOverlay
      state={state}
      respond={respondAndReply}
      stop={stop}
      pause={pause}
      resume={resume}
      retry={retry}
      takeover={takeover}
      dismiss={dismiss}
      toggleLog={toggleLog}
    />
  );
});

UiControlMount.displayName = "UiControlMount";

export default UiControlMount;
